import { PageItemMedia } from "funkis-template/models/player";
import _ from "lodash";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { selectSelectedLanguage } from "./player";

export type Subtitle = {
	id: string;
	src: string;
	enter: number;
	exit: number;
	text: string;
};

const selectAllSubtitles = (state: any) => state.subtitles.items;

function isSubtitleRelatedToSrc(
	subtitle: Subtitle | undefined,
	media: PageItemMedia | undefined,
	selectedLanguage: string
): boolean {
	if (!subtitle || !media) return false;

	if (_.isString(media)) {
		return subtitle.src === media;
	} else if (_.isPlainObject(media)) {
		return media.localization?.[selectedLanguage]
			? media.localization[selectedLanguage] === subtitle.src
			: media.src === subtitle.src;
	} else {
		return false;
	}
}

const selectSubtitle: (
	state: any,
	src: PageItemMedia,
	currentTime: number
) => Subtitle | undefined = createSelector(
	[
		selectSelectedLanguage,
		selectAllSubtitles,
		(_state: any, src: PageItemMedia) => src,
		(_state: any, _src: PageItemMedia, currentTime: number) => currentTime,
	],
	(
		selectedLanguage: string,
		subtitles: Subtitle[],
		src: PageItemMedia,
		currentTime: number
	): Subtitle | undefined => {
		const subtitlesForVideoId = subtitles.filter((subtitle) =>
			isSubtitleRelatedToSrc(subtitle, src, selectedLanguage)
		);

		const subtitle = subtitlesForVideoId.find(
			(subtitle) =>
				currentTime >= subtitle.enter && currentTime <= subtitle.exit
		);

		return subtitle;
	}
);

export const useSubtitle = (src: PageItemMedia, time: number) => {
	return useSelector((state) => selectSubtitle(state, src, time), _.isEqual);
};

const selectSubtitles: (state: any, src: PageItemMedia) => Subtitle[] =
	createSelector(
		[
			selectSelectedLanguage,
			selectAllSubtitles,
			(_state: any, src: PageItemMedia) => src,
		],
		(
			selectedLanguage: string,
			subtitles: Subtitle[],
			src: PageItemMedia
		): Subtitle[] => {
			const subtitlesForVideoId = subtitles.filter((subtitle) =>
				isSubtitleRelatedToSrc(subtitle, src, selectedLanguage)
			);

			return subtitlesForVideoId;
		}
	);

export const useSubtitles = (src: PageItemMedia) => {
	return useSelector((state) => selectSubtitles(state, src), _.isEqual);
};
